import { Settings, LogOut, User } from "lucide-react";
import type { NavSection, NavItem, UserPermission } from "@/shared/types";
import {
  getModulePrefix,
  hasModuleAccess,
  canAccessTransfersNav,
  canAccessInventoryNav,
  isBranchAccountUser,
  canAccessCkPreparedItemRoutes,
  canAccessRequestsSentRoutes,
} from "@/shared/constants";
import { IconPOForNav } from "@/assets/svgs/iconPO";
import { IconDashboardForNav } from "@/assets/svgs/iconDashboard";
import { IconInventoryForNav } from "@/assets/svgs/iconInventory";
import { IconRawMaterialsForNav } from "@/assets/svgs/iconRawMaterials";
import { IconSupplierForNav } from "@/assets/svgs/iconSupplier";
import { IconTransferForNav } from "@/assets/svgs/iconTransfer";
import { IconIncomingForNav } from "@/assets/svgs/iconIncoming";
import { IconSentRequestForNav } from "@/assets/svgs/iconSentRequest";
import { IconProducedOutputForNav } from "@/assets/svgs/iconProducedOutput";

export const navigationConfig: NavSection[] = [
  {
    id: "main",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        labelKey: "nav.dashboard",
        icon: IconDashboardForNav as NavItem["icon"],
        href: "/dashboard",
      },
    ],
  },
  {
    id: "stock-management",
    title: "Stock Management",
    titleKey: "nav.stockManagement",
    items: [
      {
        id: "inventory",
        label: "Inventory",
        labelKey: "nav.inventory",
        icon: IconInventoryForNav as NavItem["icon"],
        href: "/inventory",
        module: "inventory",
      },
      {
        id: "order-management",
        label: "Order Management",
        labelKey: "nav.orderManagement",
        icon: IconPOForNav as NavItem["icon"],
        children: [
          {
            id: "purchase-orders",
            label: "Purchase Orders",
            labelKey: "nav.purchaseOrders",
            icon: IconPOForNav as NavItem["icon"],
            href: "/purchase-orders",
            module: "purchaseOrders",
          },
          {
            id: "receiving-qc",
            label: "Receiving & QC",
            labelKey: "nav.receivingQc",
            icon: IconPOForNav as NavItem["icon"],
            href: "/receiving-qc",
            module: "receiving",
          },
        ],
      },
    ],
  },
  {
    id: "preparation-management",
    title: "Preparation Management",
    titleKey: "nav.preparationManagement",
    items: [
      {
        id: "produced-output",
        label: "Produced Output",
        labelKey: "nav.producedOutput",
        icon: IconProducedOutputForNav as NavItem["icon"],
        href: "/preparation-management",
        module: "items",
      },
    ],
  },
  {
    id: "transfers-management",
    title: "Transfers Management",
    titleKey: "nav.transfersManagement",
    breadcrumbParentLabelKey: "nav.centralKitchen",
    items: [
      {
        id: "transfers",
        label: "Transfers",
        labelKey: "nav.transfers",
        breadcrumbLabelKey: "nav.transfersManagement",
        icon: IconTransferForNav as NavItem["icon"],
        href: "/transfers",
        module: "transfers",
      },
      {
        id: "prepared-item-requests",
        label: "Incoming Requests",
        labelKey: "nav.incomingRequests",
        icon: IconIncomingForNav as NavItem["icon"],
        href: "/prepared-item-requests",
        module: "ckPreparedItemRequests",
      },
      {
        id: "requests-sent",
        label: "Requests Sent",
        labelKey: "nav.requestsSent",
        icon: IconSentRequestForNav as NavItem["icon"],
        href: "/requests-sent",
        module: "requestsSent",
      },
    ],
  },
  {
    id: "data-management",
    title: "Data Management",
    titleKey: "nav.dataManagement",
    items: [
      {
        id: "items",
        label: "Items",
        labelKey: "nav.items",
        icon: IconRawMaterialsForNav as NavItem["icon"],
        href: "/items",
        children: [
          {
            id: "raw-materials",
            label: "Raw Materials",
            labelKey: "nav.rawMaterials",
            icon: IconRawMaterialsForNav as NavItem["icon"],
            href: "/items/raw-materials",
            module: "items",
          },
          {
            id: "prepared-items",
            label: "Prepared Items",
            labelKey: "nav.preparedItems",
            icon: IconRawMaterialsForNav as NavItem["icon"],
            href: "/items/prepared-items",
            module: "items",
          },
        ],
      },
      {
        id: "suppliers",
        label: "Suppliers",
        labelKey: "nav.suppliers",
        icon: IconSupplierForNav as NavItem["icon"],
        href: "/suppliers",
        module: "suppliers",
      },
      // {
      //   id: "components",
      //   label: "components",
      //   labelKey: "nav.components",
      //   icon: ShoppingCart,
      //   href: "/components-test",
      // },
    ],
  },
];

// export function getTransfersBreadcrumbParentLabelKey(
//   userPermissions: readonly UserPermission[],
// ): string {
//   return isBranchTransferUser(userPermissions)
//     ? "nav.branch"
//     : "nav.centralKitchen";
// }

/** Branch accounts open branch transfers; kitchen accounts open central-kitchen transfers. */
export function getTransfersNavHref(isBranchUser: boolean): string {
  return isBranchUser ? "/branch-transfers" : "/transfers";
}

export function isTransfersNavPath(pathname: string): boolean {
  return (
    pathname.startsWith("/transfers") ||
    pathname.startsWith("/branch-transfers")
  );
}

export const bottomNavigationConfig: NavItem[] = [
  {
    id: "profile",
    label: "Profile",
    labelKey: "nav.profile",
    icon: User,
    href: "/profile",
  },
  {
    id: "settings",
    label: "Settings",
    labelKey: "nav.settings",
    icon: Settings,
    href: "/settings",
  },
  {
    id: "logout",
    label: "Log out",
    labelKey: "nav.logout",
    icon: LogOut,
  },
];

export { isBranchAccountUser };

export function isNavItemVisible(
  item: NavItem,
  userPermissions: readonly UserPermission[],
  isBranchUser = false,
): boolean {
  if (!item.module) {
    return true;
  }

  if (item.module === "transfers") {
    return canAccessTransfersNav(userPermissions);
  }

  if (item.module === "ckPreparedItemRequests") {
    return canAccessCkPreparedItemRoutes(userPermissions, isBranchUser);
  }

  if (item.module === "requestsSent") {
    return canAccessRequestsSentRoutes(userPermissions, isBranchUser);
  }

  if (item.module === "inventory") {
    return canAccessInventoryNav(userPermissions);
  }

  return hasModuleAccess(userPermissions, getModulePrefix(item.module));
}

export const filterNavByPermissions = (
  items: NavItem[],
  userPermissions: readonly UserPermission[],
  isBranchUser = false,
): NavItem[] => {
  return items
    .filter((item) => isNavItemVisible(item, userPermissions, isBranchUser))
    .map((item) => ({
      ...item,
      href:
        item.id === "transfers" && item.module === "transfers"
          ? getTransfersNavHref(isBranchUser)
          : item.href,
      children: item.children
        ? filterNavByPermissions(item.children, userPermissions, isBranchUser)
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);
};

export const filterSectionsByPermissions = (
  sections: NavSection[],
  userPermissions: readonly UserPermission[],
  isBranchUser = false,
): NavSection[] => {
  return sections
    .map((section) => ({
      ...section,
      breadcrumbParentLabelKey:
        section.breadcrumbParentLabelKey,
      items: filterNavByPermissions(section.items, userPermissions, isBranchUser),
    }))
    .filter((section) => section.items.length > 0);
};
